/// <amd-module name="Wit/Common/SprintEditorDialog/SprintEditorFormValidators" />

import { equals, format } from "VSS/Core/Util/String";
import { WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import * as Resources from "Wit/Common/SprintEditorDialog/Resources";

export const VALID_NAME_REGEX = /^[^\\\/$?*:"&<>#%|\+]*$/;
export const ILLEGAL_REGEX =
    /^(prn|com1|com2|com3|com4|com5|com6|com7|com8|com9|com10|lpt1|lpt2|lpt3|lpt4|lpt5|lpt6|lpt7|lpt8|lpt9|nul|con|aux|\.|\.\.)$/;
export const MAX_NAME_LENGTH = 255;

export interface WorkItemClassificationNodeWithParent extends WorkItemClassificationNode {
    parent?: WorkItemClassificationNodeWithParent;
}

export function validateName(name: string, parentNode: WorkItemClassificationNodeWithParent): string | undefined {
    if (!name) {
        return Resources.RequiredErrorMessage;
    }

    if (name.length > MAX_NAME_LENGTH) {
        return format(Resources.NameTooLongErrorMessage, MAX_NAME_LENGTH.toString());
    }

    if (!VALID_NAME_REGEX.test(name)) {
        return Resources.NameInvalidCharactersErrorMessage;
    }

    if (ILLEGAL_REGEX.test(name)) {
        return Resources.NameInvalidNameErrorMessage;
    }

    if (parentNode && parentNode.children) {
        for (const sibling of parentNode.children) {
            if (equals(sibling.name, name, true /* ignorecase */)) {
                return Resources.NameAlreadyExistsErrorMessage;
            }
        }
    }
}

export function validateStartDate(startDate: Date | undefined): string | undefined {
    if (!startDate) {
        return Resources.ValidDateRequired;
    }
}

export function validateEndDate(startDate: Date | undefined, endDate: Date | undefined): string | undefined {
    if (!endDate) {
        return Resources.ValidDateRequired;
    }

    if (startDate && startDate.getTime() > endDate.getTime()) {
        return Resources.SelectedDateOutOfRange;
    }
}

export function validateParentIterationValue(
    parentPath: string,
    parentNode?: WorkItemClassificationNodeWithParent,
    backlogIterationNode?: WorkItemClassificationNodeWithParent
): string | undefined {
    if (!parentPath) {
        return Resources.RequiredErrorMessage;
    }

    if (!parentNode) {
        return Resources.IterationDoesNotExistErrorMessage;
    }

    if (
        backlogIterationNode &&
        !equals(backlogIterationNode.identifier, parentNode.identifier, true /* ignorecase */) &&
        !isDescendant(backlogIterationNode, parentNode)
    ) {
        return Resources.SelectedNodeNotDescendantError;
    }
}

function isDescendant(node: WorkItemClassificationNodeWithParent, childNode: WorkItemClassificationNodeWithParent): boolean {
    let currentNode = childNode.parent;
    while (currentNode) {
        if (node.identifier === currentNode.identifier) {
            break;
        }

        currentNode = currentNode.parent;
    }

    return !!currentNode;
}

export function validateSelectedIteration(
    selectedPath: string,
    selectedPaths: string[],
    selectedNode?: WorkItemClassificationNodeWithParent,
    backlogIterationNode?: WorkItemClassificationNodeWithParent
): string | undefined {
    if (!selectedPath) {
        return Resources.RequiredErrorMessage;
    }

    if (!selectedNode) {
        return Resources.IterationDoesNotExistErrorMessage;
    }

    if (!selectedNode.parent) {
        return Resources.RootIterationSelectedMessage;
    }

    if (backlogIterationNode && !isDescendant(backlogIterationNode, selectedNode)) {
        return Resources.SelectedNodeNotDescendantError;
    }

    if (selectedPaths.some(existingPath => equals(existingPath, selectedPath, true))) {
        return Resources.IterationAlreadySelectedErrorMessage;
    }
}
